import * as Sentry from '@sentry/browser';
import { useFlags } from 'launchdarkly-react-client-sdk';
import get from 'lodash/get';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RouteComponentProps, useLocation } from 'react-router-dom';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { navigationService, toastNotificationsService } from 'src/config/rootService';
import { CareOption } from 'src/generated/webapp_gql';
import { useAcceptCareRecommendationMutation } from 'src/modules/onboarding/documents/acceptCareRecommendationMutation.generated';
import { ProviderType } from 'src/modules/user/models/user';
import {
    getAdditionalInfoProvided,
    getMostRecentCareRecommendation,
} from 'src/modules/user/selectors/userSelectors';
import { getSessionUtilizationInfo } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import { analyticsClient } from 'src/shared/services/analytics';

import { AdditionalInfo } from '../components/AdditionalInfo';
import { MatchedProviderSelection } from '../components/MatchedProviderSelection';
import { WorkWithProviderIntro } from '../components/WorkWithProviderIntro';
import { ProvidersPageName } from '../events';
import { useMatchedProvider } from 'src/modules/providers/hooks/useProvider';
import { captureException } from 'src/shared/helpers/captureException';

enum Step {
    INTRO = 'intro',
    ADDITIONAL_INFO = 'additionalInfo',
    CHOOSE = 'choose',
}

const WorkWithMentalHealthCoachPage: React.FC<RouteComponentProps> = ({ history }) => {
    const { t } = useTranslation();
    const careRecommendation = useSelector(getMostRecentCareRecommendation);
    const [step, setStep] = useState(Step.INTRO);
    const {
        refetchMatchedProviders,
        removeProvider,
        addProvider,
        matchedPreferenceProviders,
        notMatchedPreferenceProviders,
        isMatchedProvidersLoading,
        matchedProvidersError,
        allowsFiltering,
        matchedAttempt,
    } = useMatchedProvider({
        skip: !careRecommendation?.uuid || step !== Step.CHOOSE,
        matchVariables: {
            careRecommendationUuid: careRecommendation?.uuid!,
            providerType: CareOption.MentalHealthCoaching,
        },
    });

    const flags = useFlags();
    const { showDemographicsMhcWeb } = flags;

    const location = useLocation<{ coachToRemoveUuid?: string; onboarding?: boolean }>();
    const coachToRemoveUuid = location?.state?.coachToRemoveUuid;

    const [acceptCareRecommendation] = useAcceptCareRecommendationMutation({
        client: appClient,
    });

    const additionalInfoProvided = useSelector(getAdditionalInfoProvided);
    const { coveredCoachingVisits, coachingVisitsRemaining, resetDate } =
        useSelector(getSessionUtilizationInfo);

    const [selectedCoachUuid, setSelectedCoachUuid] = useState<string>();

    useEffect(() => {
        if (selectedCoachUuid) {
            navigateToCalendar(selectedCoachUuid);
        }
    }, [selectedCoachUuid]);

    const navigateToCalendar = async (coachUuid: string) => {
        if (!coachUuid) {
            return;
        }

        try {
            if (coachToRemoveUuid) {
                await removeProvider(coachToRemoveUuid);
            }

            await addProvider(coachUuid, matchedAttempt);

            navigationService.navigate('/schedule-session/calendar', {
                coachUuid,
                isFirstSession: coveredCoachingVisits === coachingVisitsRemaining,
                removeCoachAssignmentOnBackPress: true,
                disableBack: coachToRemoveUuid ? true : false,
                onboarding: location.state?.onboarding || false,
            });
        } catch (error) {
            setSelectedCoachUuid(undefined);
        }
    };

    const handleCoachPress = async (coachUuid: string) => {
        if (selectedCoachUuid) {
            return;
        }

        analyticsClient.trackClick('Providers: Choose provider', {
            page_name: ProvidersPageName.ChooseYourMentalHealthCoach,
        });

        setSelectedCoachUuid(coachUuid);
    };

    const handleContinue = async () => {
        analyticsClient.trackClick('Providers: Match a mental health coach', {
            page_name: ProvidersPageName.WorkWithMentalHealthCoach,
        });

        try {
            // for users that have not gone through the new onboarding so dont have any care recs
            if (!careRecommendation?.uuid) {
                return;
            }

            // if the most recent care recommendation has been already accepted just continue
            if (careRecommendation?.acceptedAt) {
                return;
            }

            const res = await acceptCareRecommendation({
                variables: {
                    uuid: careRecommendation.uuid,
                    outcome: CareOption.MentalHealthCoaching,
                },
            });
            const careUuid = get(res, 'data.acceptCareRecommendation.careRecommendation.uuid');
            if (!careUuid) {
                throw new Error('Failed to accept care recommendation');
            }
        } catch (error) {
            toastNotificationsService.renderErrorToast(config.messages.genericError);

            Sentry.withScope(scope => {
                scope.setExtra('error_info', 'Error: failed to accept a care recommendation');

                Sentry.captureException(error);
            });
            captureException(error, {
                error_info: 'Error: failed to accept a care recommendation',
            });
        } finally {
            setStep(additionalInfoProvided ? Step.CHOOSE : Step.ADDITIONAL_INFO);
        }
    };

    if (step === Step.INTRO) {
        return (
            <WorkWithProviderIntro
                providerType={ProviderType.MENTAL_HEALTH_COACH}
                title={t('providers:mental_health_coach.title')}
                description={t('providers:mental_health_coach.description', {
                    coveredCoachingVisits,
                    date: resetDate.format('MMM Do, YYYY'),
                })}
                buttonText={t('providers:mental_health_coach.button')}
                onChooseProviderClick={handleContinue}
                onNotNowClick={() => {
                    history.push('/dashboard');
                }}
            />
        );
    }

    if (step === Step.ADDITIONAL_INFO) {
        return <AdditionalInfo onSubmit={() => setStep(Step.CHOOSE)} />;
    }

    const showPreferenceFilters = !!(showDemographicsMhcWeb && allowsFiltering);

    if (step === Step.CHOOSE) {
        return (
            <MatchedProviderSelection
                providerTypeToDisplay={ProviderType.MENTAL_HEALTH_COACH}
                onProviderPress={handleCoachPress}
                selectedProvider={selectedCoachUuid}
                loading={isMatchedProvidersLoading}
                error={matchedProvidersError}
                showFilters={showPreferenceFilters}
                matchedProviders={matchedPreferenceProviders}
                notMatchedProviders={notMatchedPreferenceProviders}
                refetchMatchedProviders={races => {
                    if (careRecommendation?.uuid) {
                        refetchMatchedProviders({
                            careRecommendationUuid: careRecommendation.uuid,
                            providerType: CareOption.MentalHealthCoaching,
                            races,
                        });
                    }
                }}
                onBackPress={() => {
                    setStep(Step.ADDITIONAL_INFO);
                }}
                showBackButton={location.state?.onboarding}
                headerText={t('providers:provider_selection.title.not_matched.mental_health_coach')}
                subTitleText={t('providers:provider_selection.description.virtual')}
            />
        );
    }

    return null;
};

export default WorkWithMentalHealthCoachPage;
